import { useEffect, useRef, useState } from 'react';
import { Send, Terminal, UserCheck, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GREETING = {
  role: 'assistant',
  content: "Hey! I'm the Next Wave Tech Help AI. Tell me what's going on — a slow PC, WiFi trouble, a website project — and I'll help right here. No account needed.",
};

const QUICK_PROMPTS = [
  { id: 'chat-prompt-pc-slow', label: 'My PC is running slow' },
  { id: 'chat-prompt-email-sec', label: 'My emails land in spam' },
  { id: 'chat-prompt-web-build', label: 'I need a website for my business' },
];

function Bubble({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap border ${
          isUser ? 'border-[#00f0ff]/60 bg-[#00f0ff]/10 text-white' : 'border-[#ff00ff]/40 bg-[#ff00ff]/5 text-slate-100'
        }`}
      >
        <p className="font-tech text-[10px] uppercase tracking-widest mb-1 opacity-70">{isUser ? 'You' : 'NWTH AI'}</p>
        {msg.content}
      </div>
    </div>
  );
}

export function GuestChatConsole({ inputRef, onDispatch }) {
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => `guest_${Math.random().toString(36).slice(2, 12)}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  const send = async (text) => {
    const content = text.trim();
    if (!content || sending) return;
    const next = [...messages, { role: 'user', content }];
    setMessages(next);
    setInput('');
    setSending(true);
    try {
      const res = await fetch(`${API}/widget/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: content, session_id: sessionId, history: next.slice(1, -1) }),
      });
      const data = await res.json().catch(() => ({}));
      const reply = res.ok ? data.response : "Sorry, I couldn't connect just now. Tap 'Get a technician on this' and a human will pick it up.";
      setMessages((m) => [...m, { role: 'assistant', content: reply }]);
    } catch (err) {
      console.error('Guest chat error:', err);
      setMessages((m) => [...m, { role: 'assistant', content: "I'm having trouble connecting. You can still send this to a technician below." }]);
    } finally {
      setSending(false);
    }
  };

  const transcript = messages.slice(1);
  const hasChatted = transcript.some((m) => m.role === 'user');

  return (
    <div className="nw-card relative bg-[#07071a]/95 flex flex-col h-[520px]" data-testid="guest-ai-chat-terminal">
      <div className="nw-scanlines absolute inset-0 opacity-40" />
      <div className="relative flex items-center justify-between px-5 py-3 border-b border-[#00f0ff]/30">
        <div className="flex items-center gap-2 font-tech text-xs uppercase tracking-widest text-[#00f0ff]">
          <Terminal size={14} /> nwth://ai-console
        </div>
        <span className="flex items-center gap-2 font-tech text-[10px] uppercase tracking-widest text-[#39ff88]">
          <span className="w-2 h-2 bg-[#39ff88] animate-pulse" /> Online
        </span>
      </div>
      <div ref={scrollRef} className="relative flex-1 overflow-y-auto p-5 space-y-4" data-testid="guest-ai-chat-messages">
        {messages.map((m, i) => <Bubble key={`${i}-${m.role}`} msg={m} />)}
        {sending && (
          <p className="font-tech text-xs text-[#ff00ff] flex items-center gap-2" data-testid="guest-ai-typing">
            <Loader2 size={14} className="animate-spin" /> thinking<span className="nw-caret">_</span>
          </p>
        )}
        {!hasChatted && (
          <div className="flex flex-wrap gap-2 pt-2">
            {QUICK_PROMPTS.map((p) => (
              <button key={p.id} onClick={() => send(p.label)} data-testid={p.id}
                className="nw-btn px-3 py-1.5 border border-[#00f0ff]/40 text-xs text-[#00f0ff] hover:border-[#00f0ff] font-tech">
                {p.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="relative border-t border-[#00f0ff]/30 p-4 space-y-3">
        <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="flex gap-2">
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Describe your issue…"
            className="flex-1 bg-[#0a0a1f] border border-[#00f0ff]/40 focus:border-[#00f0ff] focus:outline-none px-4 py-3 text-sm text-white placeholder:text-slate-500 rounded-none"
            data-testid="guest-ai-chat-input"
          />
          <button type="submit" disabled={sending || !input.trim()} aria-label="Send"
            className="nw-btn px-4 bg-[#00f0ff] text-[#0a0a1f] disabled:opacity-40" data-testid="guest-ai-chat-send-btn">
            <Send size={18} />
          </button>
        </form>
        <button
          onClick={() => onDispatch(transcript)}
          className="nw-btn w-full flex items-center justify-center gap-2 py-2.5 border border-[#ff00ff] text-[#ff00ff] hover:bg-[#ff00ff] hover:text-[#0a0a1f] font-tech uppercase tracking-wider text-xs"
          data-testid="guest-ai-dispatch-tech-btn"
        >
          <UserCheck size={16} /> Get a technician on this
        </button>
      </div>
    </div>
  );
}
